/**
 * Inline products carousel — add-to-cart handler.
 *
 * One global delegated listener for all `.inline-products-cta-add` buttons.
 * - POSTs to /cart/add.js (Shopify standard route).
 * - On success: swaps the button label to the success copy for ~1.8s, then restores it.
 * - On error: shows the error copy for ~1.8s.
 * - Dispatches a `cart:item-added` CustomEvent on document so the theme's mini-cart can refresh
 *   if it listens to that event. This is intentionally a no-op for themes that don't.
 *
 * All user-facing strings come from the button's data-* attributes (rendered server-side via locales).
 */
(function () {
  'use strict';

  const SUCCESS_TIMEOUT = 1800;
  const ERROR_TIMEOUT = 1800;

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('.inline-products-cta-add');
    if (!btn) return;
    if (btn.classList.contains('is-loading')) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const labelEl = btn.querySelector('.inline-products-cta-label') || btn;
    const originalLabel = btn.dataset.addLabel || labelEl.textContent;
    const successLabel = btn.dataset.successLabel || originalLabel;
    const errorLabel = btn.dataset.errorLabel || originalLabel;

    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart add failed (${response.status})`);
      }

      const payload = await response.json();

      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      labelEl.textContent = successLabel;

      document.dispatchEvent(
        new CustomEvent('cart:item-added', {
          detail: { items: payload.items || [], source: 'inline-products' },
        })
      );

      // Some Shopify themes use a different event name — fire both for compatibility.
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { detail: { source: 'inline-products' } })
      );

      window.setTimeout(() => {
        btn.classList.remove('is-success');
        labelEl.textContent = originalLabel;
        btn.disabled = false;
      }, SUCCESS_TIMEOUT);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[inline-products] add to cart error:', err);
      btn.classList.remove('is-loading');
      btn.classList.add('is-error');
      labelEl.textContent = errorLabel;

      window.setTimeout(() => {
        btn.classList.remove('is-error');
        labelEl.textContent = originalLabel;
        btn.disabled = false;
      }, ERROR_TIMEOUT);
    }
  });
})();
